/**
 * Item type categories
 */
export const ITEM_TYPES = {
  CONSUMABLE: 'consumable',
  WEAPON: 'weapon',
  ARMOR: 'armor',
  KEY: 'key',
  QUEST: 'quest',
  TREASURE: 'treasure'
};

/**
 * Rarity tiers with display colors
 */
export const RARITY_TIERS = {
  common:    { label: 'Common',    color: '#9d9d9d' },
  uncommon:  { label: 'Uncommon',  color: '#1eff00' },
  rare:      { label: 'Rare',      color: '#0070ff' },
  epic:      { label: 'Epic',      color: '#a335ee' },
  legendary: { label: 'Legendary', color: '#ff8000' },
};

/**
 * All equipment slot keys and their display labels.
 */
export const EQUIPMENT_SLOTS = {
  head:     'Head',
  shoulders:'Shoulders',
  body:     'Body',
  hands:    'Hands',
  belt:     'Belt',
  legs:     'Legs',
  feet:     'Feet',
  back:     'Back',
  mainhand: 'Main Hand',
  offhand:  'Off Hand',
  neck:     'Neck',
  ring1:    'Ring',
  ring2:    'Ring',
};

/**
 * Item definitions registry.
 * color: {r,g,b} used for quickbar tinting and fallback visual.
 * icon: path relative to public/ for pixel art icon.
 * rarity: tier key from RARITY_TIERS.
 * weight: item weight in abstract units (affects carry capacity).
 * buyPrice: gold cost at merchant (null = not buyable).
 * sellPrice: explicit sell value (for treasures); otherwise sell = 50% of buyPrice.
 * slot: equipment slot key (head, body, feet, legs, hands, shoulders, belt, back, mainhand, offhand, neck, ring).
 *       Items with slot='ring' can equip into ring1 or ring2.
 */
export const ITEM_DEFS = {

  // ── Consumables ──────────────────────────────────────────

  'health-potion': {
    name: 'Health Potion',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A small vial of red liquid that restores vitality.',
    color: { r: 220, g: 50, b: 50 },
    icon: 'icons/items/P_Red01.png',
    rarity: 'common',
    buyPrice: 25,
    effect: { heal: 30 },
    stackable: true,
    weight: 0.5
  },
  'large-health-potion': {
    name: 'Large Health Potion',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A generously sized flask brimming with healing elixir.',
    color: { r: 180, g: 30, b: 30 },
    icon: 'icons/items/P_Red07.png',
    rarity: 'uncommon',
    buyPrice: 60,
    effect: { heal: 60 },
    stackable: true,
    weight: 0.8
  },
  'mana-potion': {
    name: 'Mana Potion',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A shimmering blue potion that restores magical energy.',
    color: { r: 50, g: 100, b: 220 },
    icon: 'icons/items/P_Blue02.png',
    rarity: 'common',
    buyPrice: 30,
    effect: { restoreMana: 40 },
    stackable: true,
    weight: 0.5
  },
  'stamina-potion': {
    name: 'Stamina Potion',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A bright yellow tonic that reinvigorates the body.',
    color: { r: 220, g: 200, b: 50 },
    icon: 'icons/items/P_Yellow03.png',
    rarity: 'common',
    buyPrice: 20,
    effect: { restoreStamina: 50 },
    stackable: true,
    weight: 0.5
  },
  'large-mana-potion': {
    name: 'Large Mana Potion',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A deep azure flask brimming with arcane energy.',
    color: { r: 30, g: 80, b: 200 },
    icon: 'icons/items/P_Blue02.png',
    rarity: 'uncommon',
    buyPrice: 75,
    effect: { restoreMana: 80 },
    stackable: true,
    weight: 0.8
  },
  'large-stamina-potion': {
    name: 'Large Stamina Potion',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A hearty golden brew that floods the limbs with renewed vigor.',
    color: { r: 200, g: 180, b: 30 },
    icon: 'icons/items/P_Yellow03.png',
    rarity: 'uncommon',
    buyPrice: 50,
    effect: { restoreStamina: 100 },
    stackable: true,
    weight: 0.8
  },
  'elixir': {
    name: 'Elixir of Vigor',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A rare green elixir that restores even the gravest wounds.',
    color: { r: 50, g: 180, b: 80 },
    icon: 'icons/items/P_Green03.png',
    rarity: 'rare',
    buyPrice: 150,
    effect: { heal: 100 },
    stackable: true,
    weight: 0.5
  },
  'antidote': {
    name: 'Antidote',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A bitter yellow tonic. Cures what ails you.',
    color: { r: 220, g: 200, b: 50 },
    icon: 'icons/items/P_Yellow03.png',
    rarity: 'common',
    buyPrice: 15,
    effect: { heal: 20 },
    stackable: true,
    weight: 0.5
  },
  'fire-bomb': {
    name: 'Fire Bomb',
    type: ITEM_TYPES.CONSUMABLE,
    description: 'A volatile orange concoction. Handle with care.',
    color: { r: 220, g: 130, b: 40 },
    icon: 'icons/items/P_Orange03.png',
    rarity: 'uncommon',
    buyPrice: 45,
    effect: { heal: 50 },
    stackable: true,
    weight: 1.0
  },

  // ── Weapons (slot: mainhand) ───────────────────────────────

  'iron-sword': {
    name: 'Iron Sword',
    type: ITEM_TYPES.WEAPON,
    description: 'A sturdy blade forged from common iron.',
    color: { r: 180, g: 180, b: 200 },
    icon: 'icons/items/W_Sword004.png',
    rarity: 'common',
    weight: 4.0,
    buyPrice: 100,
    stats: { attackPower: 5, str: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_longsword', variant: 'longsword' },
    attackAnims: ['weapon-slash', 'weapon-slash-reverse']
  },
  'steel-blade': {
    name: 'Steel Blade',
    type: ITEM_TYPES.WEAPON,
    description: 'Tempered steel with a keen edge.',
    color: { r: 190, g: 195, b: 210 },
    icon: 'icons/items/W_Sword009.png',
    rarity: 'uncommon',
    weight: 4.5,
    buyPrice: 180,
    stats: { attackPower: 8, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_longsword', variant: 'longsword' },
    attackAnims: ['weapon-slash', 'weapon-slash-reverse']
  },
  'golden-sword': {
    name: 'Golden Sword',
    type: ITEM_TYPES.WEAPON,
    description: 'An ornate blade gilded with pure gold. Surprisingly sharp.',
    color: { r: 255, g: 215, b: 0 },
    icon: 'icons/items/W_Gold_Sword.png',
    rarity: 'rare',
    weight: 5.0,
    buyPrice: 400,
    stats: { attackPower: 12, str: 3 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_rapier', variant: 'rapier' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'frost-blade': {
    name: 'Frost Blade',
    type: ITEM_TYPES.WEAPON,
    description: 'An enchanted sword radiating bitter cold.',
    color: { r: 120, g: 180, b: 255 },
    icon: 'icons/items/W_Sword018.png',
    rarity: 'epic',
    weight: 4.0,
    buyPrice: 800,
    stats: { attackPower: 15, str: 3, int: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_glowsword', variant: 'blue' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'fire-staff': {
    name: 'Fire Staff',
    type: ITEM_TYPES.WEAPON,
    description: 'A staff crowned with living flame.',
    color: { r: 200, g: 80, b: 30 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'rare',
    weight: 3.0,
    buyPrice: 250,
    stats: { attackPower: 8, int: 3, wis: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_magic_simple', variant: 'simple' },
    attackAnims: ['weapon-thrust', 'weapon-slash'],
    projectileType: 'fireball'
  },
  'war-axe': {
    name: 'War Axe',
    type: ITEM_TYPES.WEAPON,
    description: 'A heavy axe built for battle.',
    color: { r: 160, g: 160, b: 175 },
    icon: 'icons/items/W_Axe005.png',
    rarity: 'common',
    weight: 5.0,
    buyPrice: 120,
    stats: { attackPower: 7, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_blunt_waraxe', variant: 'waraxe' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'battle-axe': {
    name: 'Battle Axe',
    type: ITEM_TYPES.WEAPON,
    description: 'A fearsome double-headed axe wreathed in firelight.',
    color: { r: 200, g: 140, b: 40 },
    icon: 'icons/items/W_Axe010.png',
    rarity: 'uncommon',
    weight: 6.0,
    buyPrice: 280,
    stats: { attackPower: 11, str: 3 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_blunt_waraxe', variant: 'waraxe' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'iron-dagger': {
    name: 'Iron Dagger',
    type: ITEM_TYPES.WEAPON,
    description: 'A small but deadly blade. Quick to strike.',
    color: { r: 170, g: 170, b: 185 },
    icon: 'icons/items/W_Dagger003.png',
    rarity: 'common',
    weight: 1.5,
    buyPrice: 50,
    stats: { attackPower: 3, str: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_dagger', variant: 'dagger' },
    attackAnims: ['weapon-slash', 'weapon-thrust']
  },
  'golden-dagger': {
    name: 'Golden Dagger',
    type: ITEM_TYPES.WEAPON,
    description: 'A gilded ceremonial dagger. Deceptively lethal.',
    color: { r: 255, g: 200, b: 50 },
    icon: 'icons/items/W_Dagger015.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 300,
    stats: { attackPower: 9, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_dagger', variant: 'dagger' },
    attackAnims: ['weapon-slash', 'weapon-thrust']
  },
  'hunter-bow': {
    name: "Hunter's Bow",
    type: ITEM_TYPES.WEAPON,
    description: 'A well-crafted bow favored by woodland hunters.',
    color: { r: 180, g: 140, b: 80 },
    icon: 'icons/items/W_Bow03.png',
    rarity: 'common',
    weight: 2.5,
    buyPrice: 110,
    stats: { attackPower: 6, dex: 3 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_ranged_bow_normal', variant: 'medium' },
    attackAnims: ['weapon-shoot', 'weapon-shoot'],
    projectileType: 'arrow'
  },
  'iron-mace': {
    name: 'Iron Mace',
    type: ITEM_TYPES.WEAPON,
    description: 'A blunt weapon that crushes through armor.',
    color: { r: 165, g: 165, b: 180 },
    icon: 'icons/items/W_Mace004.png',
    rarity: 'common',
    weight: 5.0,
    buyPrice: 100,
    stats: { attackPower: 6, str: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_blunt_mace', variant: 'mace' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'iron-spear': {
    name: 'Iron Spear',
    type: ITEM_TYPES.WEAPON,
    description: 'A long-reaching spear tipped with iron.',
    color: { r: 170, g: 170, b: 185 },
    icon: 'icons/items/W_Spear005.png',
    rarity: 'common',
    weight: 3.5,
    buyPrice: 130,
    stats: { attackPower: 7, str: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_polearm_spear', variant: 'iron' },
    attackAnims: ['weapon-thrust', 'weapon-slash']
  },
  'katana': {
    name: 'Katana',
    type: ITEM_TYPES.WEAPON,
    description: 'A curved single-edged blade from distant lands.',
    color: { r: 200, g: 200, b: 220 },
    icon: 'icons/items/W_Sword004.png',
    rarity: 'rare',
    weight: 3.5,
    buyPrice: 350,
    stats: { attackPower: 11, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_katana', variant: 'katana' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'scimitar': {
    name: 'Scimitar',
    type: ITEM_TYPES.WEAPON,
    description: 'A broad curved blade favored by desert warriors.',
    color: { r: 185, g: 185, b: 200 },
    icon: 'icons/items/W_Sword009.png',
    rarity: 'uncommon',
    weight: 4.0,
    buyPrice: 200,
    stats: { attackPower: 9, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_scimitar', variant: 'scimitar' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'flail': {
    name: 'Flail',
    type: ITEM_TYPES.WEAPON,
    description: 'A spiked ball on a chain. Devastatingly unpredictable.',
    color: { r: 155, g: 155, b: 170 },
    icon: 'icons/items/W_Mace004.png',
    rarity: 'uncommon',
    weight: 5.5,
    buyPrice: 190,
    stats: { attackPower: 9, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_blunt_flail', variant: 'flail' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'crystal-staff': {
    name: 'Crystal Staff',
    type: ITEM_TYPES.WEAPON,
    description: 'A staff topped with a pulsing blue crystal.',
    color: { r: 80, g: 140, b: 255 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'rare',
    weight: 2.5,
    buyPrice: 300,
    stats: { attackPower: 10, int: 3, wis: 1 },
    slot: 'mainhand',
    lpcLayer: [
      { layerId: 'weapon_magic_diamond', variant: 'gold' },
      { layerId: 'weapon_magic_crystal', variant: 'blue' }
    ],
    attackAnims: ['weapon-thrust', 'weapon-thrust'],
    projectileType: 'icebolt'
  },
  'diamond-staff': {
    name: 'Diamond Staff',
    type: ITEM_TYPES.WEAPON,
    description: 'An ornate staff crowned with a radiant yellow gem.',
    color: { r: 200, g: 220, b: 240 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'epic',
    weight: 3.0,
    buyPrice: 600,
    stats: { attackPower: 14, int: 3, wis: 1 },
    slot: 'mainhand',
    lpcLayer: [
      { layerId: 'weapon_magic_diamond', variant: 'gold' },
      { layerId: 'weapon_magic_crystal', variant: 'yellow' }
    ],
    attackAnims: ['weapon-thrust', 'weapon-thrust'],
    projectileType: 'lightningbolt'
  },
  'gnarled-staff': {
    name: 'Gnarled Staff',
    type: ITEM_TYPES.WEAPON,
    description: 'A twisted wooden staff humming with primal magic.',
    color: { r: 140, g: 100, b: 60 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'uncommon',
    weight: 2.5,
    buyPrice: 160,
    stats: { attackPower: 7, int: 2 },
    slot: 'mainhand',
    lpcLayer: [
      { layerId: 'weapon_magic_gnarled', variant: 'medium' },
      { layerId: 'weapon_magic_crystal', variant: 'green' }
    ],
    attackAnims: ['weapon-thrust', 'weapon-thrust'],
    projectileType: 'poisonbolt'
  },
  'loop-staff': {
    name: 'Loop Staff',
    type: ITEM_TYPES.WEAPON,
    description: 'A staff with a ringed head that channels arcane energy.',
    color: { r: 160, g: 130, b: 200 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'uncommon',
    weight: 2.5,
    buyPrice: 180,
    stats: { attackPower: 8, int: 2 },
    slot: 'mainhand',
    lpcLayer: [
      { layerId: 'weapon_magic_loop', variant: 'medium' },
      { layerId: 'weapon_magic_crystal', variant: 'purple' }
    ],
    attackAnims: ['weapon-thrust', 'weapon-thrust'],
    projectileType: 'arcanebolt'
  },
  'serpent-staff': {
    name: 'Serpent Staff',
    type: ITEM_TYPES.WEAPON,
    description: 'A sinuous staff crowned with a serpentine head.',
    color: { r: 100, g: 180, b: 100 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'rare',
    weight: 2.5,
    buyPrice: 320,
    stats: { attackPower: 11, int: 3, wis: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_magic_s', variant: 'medium' },
    attackAnims: ['weapon-thrust', 'weapon-thrust'],
    projectileType: 'poisonbolt'
  },
  'magic-wand': {
    name: 'Magic Wand',
    type: ITEM_TYPES.WEAPON,
    description: 'A slender wand crackling with magical potential.',
    color: { r: 180, g: 140, b: 220 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 80,
    stats: { attackPower: 5, int: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_magic_wand', variant: 'wand' },
    attackAnims: ['weapon-slash', 'weapon-thrust'],
    projectileType: 'arcanebolt'
  },
  'halberd': {
    name: 'Halberd',
    type: ITEM_TYPES.WEAPON,
    description: 'An axe blade mounted on a long shaft. Reach and power combined.',
    color: { r: 170, g: 170, b: 185 },
    icon: 'icons/items/W_Axe005.png',
    rarity: 'uncommon',
    weight: 6.0,
    buyPrice: 240,
    stats: { attackPower: 10, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_polearm_halberd', variant: 'halberd' },
    attackAnims: ['weapon-slash', 'weapon-thrust']
  },
  'trident': {
    name: 'Trident',
    type: ITEM_TYPES.WEAPON,
    description: 'A three-pronged spear of the deep.',
    color: { r: 60, g: 130, b: 190 },
    icon: 'icons/items/W_Spear005.png',
    rarity: 'rare',
    weight: 4.0,
    buyPrice: 280,
    stats: { attackPower: 11, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_polearm_trident', variant: 'bronze' },
    attackAnims: ['weapon-thrust', 'weapon-thrust']
  },
  'dragonspear': {
    name: 'Dragonspear',
    type: ITEM_TYPES.WEAPON,
    description: 'A legendary spear said to have slain a dragon.',
    color: { r: 200, g: 60, b: 60 },
    icon: 'icons/items/W_Spear005.png',
    rarity: 'epic',
    weight: 4.5,
    buyPrice: 500,
    stats: { attackPower: 14, str: 3 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_polearm_dragonspear', variant: 'bronze' },
    attackAnims: ['weapon-thrust', 'weapon-thrust']
  },
  'longspear': {
    name: 'Longspear',
    type: ITEM_TYPES.WEAPON,
    description: 'An exceptionally long spear for keeping foes at bay.',
    color: { r: 175, g: 175, b: 190 },
    icon: 'icons/items/W_Spear005.png',
    rarity: 'uncommon',
    weight: 4.0,
    buyPrice: 170,
    stats: { attackPower: 8, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_polearm_longspear', variant: 'bronze' },
    attackAnims: ['weapon-thrust', 'weapon-thrust']
  },
  'scythe': {
    name: 'Scythe',
    type: ITEM_TYPES.WEAPON,
    description: 'A wicked curved blade on a long handle. Not just for harvests.',
    color: { r: 100, g: 100, b: 115 },
    icon: 'icons/items/W_Axe010.png',
    rarity: 'rare',
    weight: 5.0,
    buyPrice: 320,
    stats: { attackPower: 12, str: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_polearm_scythe', variant: 'scythe' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'walking-cane': {
    name: 'Walking Cane',
    type: ITEM_TYPES.WEAPON,
    description: 'A sturdy cane that doubles as a weapon in a pinch.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/W_Staff03.png',
    rarity: 'common',
    weight: 2.0,
    buyPrice: 40,
    stats: { attackPower: 3, str: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_polearm_cane', variant: 'cane' },
    attackAnims: ['weapon-thrust', 'weapon-slash']
  },
  'great-bow': {
    name: 'Great Bow',
    type: ITEM_TYPES.WEAPON,
    description: 'A massive bow that fires arrows with tremendous force.',
    color: { r: 160, g: 120, b: 60 },
    icon: 'icons/items/W_Bow03.png',
    rarity: 'rare',
    weight: 3.5,
    buyPrice: 300,
    stats: { attackPower: 12, dex: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_ranged_bow_great', variant: 'medium' },
    attackAnims: ['weapon-shoot', 'weapon-shoot'],
    projectileType: 'arrow'
  },
  'recurve-bow': {
    name: 'Recurve Bow',
    type: ITEM_TYPES.WEAPON,
    description: 'An elegantly curved bow with exceptional range.',
    color: { r: 170, g: 130, b: 70 },
    icon: 'icons/items/W_Bow03.png',
    rarity: 'uncommon',
    weight: 2.5,
    buyPrice: 180,
    stats: { attackPower: 8, dex: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_ranged_bow_recurve', variant: 'medium' },
    attackAnims: ['weapon-shoot', 'weapon-shoot'],
    projectileType: 'arrow'
  },
  'crossbow': {
    name: 'Crossbow',
    type: ITEM_TYPES.WEAPON,
    description: 'A mechanical bow that packs a devastating punch.',
    color: { r: 140, g: 110, b: 70 },
    icon: 'icons/items/W_Bow03.png',
    rarity: 'uncommon',
    weight: 4.0,
    buyPrice: 200,
    stats: { attackPower: 9, dex: 2 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_ranged_crossbow', variant: 'crossbow' },
    attackAnims: ['weapon-thrust', 'weapon-thrust'],
    projectileType: 'arrow'
  },
  'boomerang': {
    name: 'Boomerang',
    type: ITEM_TYPES.WEAPON,
    description: 'A throwing weapon that returns to the wielder.',
    color: { r: 180, g: 140, b: 80 },
    icon: 'icons/items/W_Boomerang.png',
    rarity: 'common',
    weight: 1.5,
    buyPrice: 60,
    stats: { attackPower: 4, dex: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_ranged_boomerang', variant: 'boomerang' },
    attackAnims: ['weapon-slash-reverse', 'weapon-slash-reverse']
  },
  'slingshot': {
    name: 'Slingshot',
    type: ITEM_TYPES.WEAPON,
    description: 'Simple but effective. David would be proud.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/W_Slingshot.png',
    rarity: 'common',
    weight: 0.5,
    buyPrice: 20,
    stats: { attackPower: 2, dex: 1 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_ranged_slingshot', variant: 'slingshot' },
    attackAnims: ['weapon-shoot', 'weapon-shoot'],
    projectileType: 'ball'
  },

  // ── Head ───────────────────────────────────────────────────

  'wizard-hat': {
    name: 'Wizard Hat',
    type: ITEM_TYPES.ARMOR,
    description: 'A pointy hat imbued with protective enchantments.',
    color: { r: 140, g: 100, b: 200 },
    icon: 'icons/items/lpc_wizard-hat.png',
    rarity: 'uncommon',
    weight: 1.0,
    buyPrice: 120,
    stats: { defense: 4, int: 2, wis: 1 },
    slot: 'head',
    lpcLayer: { layerId: 'hat_magic_wizard', variant: 'purple' }
  },
  'iron-helm': {
    name: 'Iron Helm',
    type: ITEM_TYPES.ARMOR,
    description: 'A solid iron helmet. Simple but effective.',
    color: { r: 160, g: 165, b: 175 },
    icon: 'icons/items/lpc_iron-helm.png',
    rarity: 'uncommon',
    weight: 3.0,
    buyPrice: 140,
    stats: { defense: 5, con: 1 },
    slot: 'head',
    lpcLayer: { layerId: 'hat_helmet_mail', variant: 'iron' }
  },
  'knight-helm': {
    name: "Knight's Helm",
    type: ITEM_TYPES.ARMOR,
    description: 'A gleaming helm worn by elite knights.',
    color: { r: 190, g: 195, b: 210 },
    icon: 'icons/items/lpc_knight-helm.png',
    rarity: 'rare',
    weight: 4.0,
    buyPrice: 280,
    stats: { defense: 8, con: 2 },
    slot: 'head',
    lpcLayer: { layerId: 'hat_helmet_mail', variant: 'steel' }
  },
  'barbarian-helm': {
    name: 'Barbarian Helm',
    type: ITEM_TYPES.ARMOR,
    description: 'A fearsome horned helm worn by northern raiders.',
    color: { r: 160, g: 140, b: 100 },
    icon: 'icons/items/lpc_barbarian-helm.png',
    rarity: 'uncommon',
    weight: 3.0,
    buyPrice: 160,
    stats: { defense: 5, str: 1 },
    slot: 'head',
    lpcLayer: { layerId: 'hat_helmet_barbarian', variant: 'iron' }
  },
  'cloth-hood': {
    name: 'Cloth Hood',
    type: ITEM_TYPES.ARMOR,
    description: 'A simple cloth hood that conceals the wearer.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/lpc_cloth-hood.png',
    rarity: 'common',
    weight: 0.5,
    buyPrice: 30,
    stats: { defense: 1, wis: 1 },
    slot: 'head',
    lpcLayer: { layerId: 'hat_hood_cloth', variant: 'brown' }
  },
  'kettle-helm': {
    name: 'Kettle Helm',
    type: ITEM_TYPES.ARMOR,
    description: 'A wide-brimmed steel helm that deflects blows.',
    color: { r: 185, g: 185, b: 200 },
    icon: 'icons/items/lpc_kettle-helm.png',
    rarity: 'uncommon',
    weight: 3.0,
    buyPrice: 150,
    stats: { defense: 5, con: 1 },
    slot: 'head',
    lpcLayer: { layerId: 'hat_helmet_kettle', variant: 'steel' }
  },

  // ── Shoulders ──────────────────────────────────────────────

  'iron-pauldrons': {
    name: 'Iron Pauldrons',
    type: ITEM_TYPES.ARMOR,
    description: 'Broad iron plates that shield the shoulders.',
    color: { r: 160, g: 165, b: 175 },
    icon: 'icons/items/lpc_iron-pauldrons.png',
    rarity: 'common',
    weight: 3.0,
    buyPrice: 90,
    stats: { defense: 3, con: 1 },
    slot: 'shoulders',
    lpcLayer: { layerId: 'shoulders_plate', variant: 'iron' }
  },
  'steel-spaulders': {
    name: 'Steel Spaulders',
    type: ITEM_TYPES.ARMOR,
    description: 'Reinforced steel shoulder guards.',
    color: { r: 180, g: 185, b: 200 },
    icon: 'icons/items/lpc_steel-spaulders.png',
    rarity: 'uncommon',
    weight: 4.0,
    buyPrice: 170,
    stats: { defense: 5, con: 1 },
    slot: 'shoulders',
    lpcLayer: { layerId: 'shoulders_plate', variant: 'steel' }
  },
  'golden-shoulders': {
    name: 'Golden Pauldrons',
    type: ITEM_TYPES.ARMOR,
    description: 'Magnificent golden shoulder plates radiating power.',
    color: { r: 255, g: 215, b: 0 },
    icon: 'icons/items/lpc_golden-shoulders.png',
    rarity: 'rare',
    weight: 5.0,
    buyPrice: 320,
    stats: { defense: 8, con: 2 },
    slot: 'shoulders',
    lpcLayer: { layerId: 'shoulders_plate', variant: 'gold' }
  },
  'leather-shoulders': {
    name: 'Leather Shoulder Pads',
    type: ITEM_TYPES.ARMOR,
    description: 'Simple leather pads that protect the shoulders.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/lpc_leather-shoulders.png',
    rarity: 'common',
    weight: 2.0,
    buyPrice: 60,
    stats: { defense: 2, end: 1 },
    slot: 'shoulders',
    lpcLayer: { layerId: 'shoulders_leather', variant: 'leather' }
  },
  'legion-shoulders': {
    name: 'Legion Pauldrons',
    type: ITEM_TYPES.ARMOR,
    description: 'Ornate bronze shoulder guards of an ancient legion.',
    color: { r: 180, g: 140, b: 60 },
    icon: 'icons/items/lpc_legion-shoulders.png',
    rarity: 'rare',
    weight: 4.0,
    buyPrice: 260,
    stats: { defense: 7, str: 1 },
    slot: 'shoulders',
    lpcLayer: { layerId: 'shoulders_legion', variant: 'bronze' }
  },
  'epaulets': {
    name: 'Gold Epaulets',
    type: ITEM_TYPES.ARMOR,
    description: 'Decorative gold shoulder ornaments denoting high rank.',
    color: { r: 255, g: 215, b: 0 },
    icon: 'icons/items/lpc_epaulets.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 280,
    stats: { defense: 4, attackPower: 3, str: 1 },
    slot: 'shoulders',
    lpcLayer: { layerId: 'shoulders_epaulets', variant: 'gold' }
  },
  'mantle': {
    name: 'Shoulder Mantle',
    type: ITEM_TYPES.ARMOR,
    description: 'A flowing cloth mantle draped over the shoulders.',
    color: { r: 100, g: 80, b: 160 },
    icon: 'icons/items/lpc_mantle.png',
    rarity: 'uncommon',
    weight: 1.5,
    buyPrice: 120,
    stats: { defense: 3, wis: 1 },
    slot: 'shoulders',
    lpcLayer: { layerId: 'shoulders_mantal', variant: 'purple' }
  },

  // ── Body ───────────────────────────────────────────────────

  'leather-armor': {
    name: 'Leather Armor',
    type: ITEM_TYPES.ARMOR,
    description: 'Tough hide stitched into a protective vest.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/lpc_leather-armor.png',
    rarity: 'common',
    weight: 3.0,
    buyPrice: 80,
    stats: { defense: 3, end: 1 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_clothes_vest_open', variant: 'brown' }
  },
  'chain-mail': {
    name: 'Chain Mail',
    type: ITEM_TYPES.ARMOR,
    description: 'Interlocking metal rings that deflect blows.',
    color: { r: 150, g: 155, b: 165 },
    icon: 'icons/items/lpc_chain-mail.png',
    rarity: 'uncommon',
    weight: 5.0,
    buyPrice: 180,
    stats: { defense: 6, con: 1 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_armour_legion', variant: 'iron' }
  },
  'plate-armor': {
    name: 'Plate Armor',
    type: ITEM_TYPES.ARMOR,
    description: 'Heavy steel plates offering superior protection.',
    color: { r: 200, g: 200, b: 215 },
    icon: 'icons/items/lpc_plate-armor.png',
    rarity: 'rare',
    weight: 8.0,
    buyPrice: 350,
    stats: { defense: 10, con: 2 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_armour_plate', variant: 'steel' }
  },
  'golden-armor': {
    name: 'Golden Armor',
    type: ITEM_TYPES.ARMOR,
    description: 'Resplendent armor fit for royalty. Magically reinforced.',
    color: { r: 255, g: 200, b: 50 },
    icon: 'icons/items/lpc_golden-armor.png',
    rarity: 'epic',
    weight: 7.0,
    buyPrice: 700,
    stats: { defense: 14, con: 2 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_armour_plate', variant: 'gold' }
  },
  'mage-robe': {
    name: 'Mage Robe',
    type: ITEM_TYPES.ARMOR,
    description: 'A flowing purple robe favored by practitioners of the arcane.',
    color: { r: 120, g: 80, b: 180 },
    icon: 'icons/items/lpc_mage-robe.png',
    rarity: 'uncommon',
    weight: 2.0,
    buyPrice: 140,
    stats: { defense: 3, attackPower: 3, int: 1 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_clothes_longsleeve', variant: 'purple' }
  },
  'tunic': {
    name: 'Tunic',
    type: ITEM_TYPES.ARMOR,
    description: 'A simple cloth tunic. Better than nothing.',
    color: { r: 139, g: 100, b: 60 },
    icon: 'icons/items/lpc_tunic.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 20,
    stats: { defense: 1, end: 1 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_clothes_shortsleeve', variant: 'brown' }
  },
  'tabard': {
    name: 'Tabard',
    type: ITEM_TYPES.ARMOR,
    description: 'A sleeveless coat worn over armor displaying allegiance.',
    color: { r: 180, g: 50, b: 50 },
    icon: 'icons/items/lpc_tabard.png',
    rarity: 'uncommon',
    weight: 1.5,
    buyPrice: 100,
    stats: { defense: 2, end: 1 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_clothes_longsleeve', variant: 'red' }
  },
  'longsleeve-shirt': {
    name: 'Longsleeve Shirt',
    type: ITEM_TYPES.ARMOR,
    description: 'A comfortable long-sleeved shirt. Stylish, at least.',
    color: { r: 80, g: 120, b: 180 },
    icon: 'icons/items/lpc_longsleeve-shirt.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 25,
    stats: { defense: 1, end: 1 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_clothes_longsleeve', variant: 'blue' }
  },
  'legion-armor': {
    name: 'Legion Armor',
    type: ITEM_TYPES.ARMOR,
    description: 'Bronze armor of an ancient legion. Tried and true.',
    color: { r: 180, g: 140, b: 60 },
    icon: 'icons/items/lpc_legion-armor.png',
    rarity: 'rare',
    weight: 6.0,
    buyPrice: 300,
    stats: { defense: 9, con: 2 },
    slot: 'body',
    lpcLayer: { layerId: 'torso_armour_legion', variant: 'bronze' }
  },

  // ── Hands ──────────────────────────────────────────────────

  'leather-gloves': {
    name: 'Leather Gloves',
    type: ITEM_TYPES.ARMOR,
    description: 'Simple gloves offering a sure grip and light protection.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/lpc_leather-gloves.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 40,
    stats: { defense: 2, dex: 1 },
    slot: 'hands',
    lpcLayer: { layerId: 'arms_gloves', variant: 'brown' }
  },
  'chain-gauntlets': {
    name: 'Chain Gauntlets',
    type: ITEM_TYPES.ARMOR,
    description: 'Mail-linked gauntlets that protect without hindering.',
    color: { r: 150, g: 155, b: 165 },
    icon: 'icons/items/lpc_chain-gauntlets.png',
    rarity: 'uncommon',
    weight: 2.0,
    buyPrice: 130,
    stats: { defense: 4, str: 1 },
    slot: 'hands',
    lpcLayer: { layerId: 'arms_armour', variant: 'iron' }
  },
  'plate-gauntlets': {
    name: 'Plate Gauntlets',
    type: ITEM_TYPES.ARMOR,
    description: 'Heavy plated gauntlets forged for war.',
    color: { r: 190, g: 195, b: 210 },
    icon: 'icons/items/lpc_plate-gauntlets.png',
    rarity: 'rare',
    weight: 3.0,
    buyPrice: 260,
    stats: { defense: 7, str: 1 },
    slot: 'hands',
    lpcLayer: { layerId: 'arms_armour', variant: 'steel' }
  },
  'iron-bracers': {
    name: 'Iron Bracers',
    type: ITEM_TYPES.ARMOR,
    description: 'Metal bracers that protect the forearms.',
    color: { r: 160, g: 165, b: 175 },
    icon: 'icons/items/lpc_iron-bracers.png',
    rarity: 'uncommon',
    weight: 2.0,
    buyPrice: 100,
    stats: { defense: 3, str: 1 },
    slot: 'hands',
    lpcLayer: { layerId: 'arms_bracers', variant: 'iron' }
  },

  // ── Belt ───────────────────────────────────────────────────

  'leather-belt': {
    name: 'Leather Belt',
    type: ITEM_TYPES.ARMOR,
    description: 'A sturdy belt with loops for tools and pouches.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/Sh_Belt01.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 30,
    stats: { defense: 1, end: 1 },
    slot: 'belt',
    lpcLayer: { layerId: 'belt_leather', variant: 'brown' }
  },
  'reinforced-belt': {
    name: 'Reinforced Belt',
    type: ITEM_TYPES.ARMOR,
    description: 'A belt reinforced with metal studs.',
    color: { r: 160, g: 140, b: 100 },
    icon: 'icons/items/Sh_Belt02.png',
    rarity: 'uncommon',
    weight: 1.5,
    buyPrice: 100,
    stats: { defense: 3, con: 1 },
    slot: 'belt',
    lpcLayer: { layerId: 'belt_double', variant: 'leather' }
  },
  'war-girdle': {
    name: 'War Girdle',
    type: ITEM_TYPES.ARMOR,
    description: 'A broad armored belt worn by seasoned warriors.',
    color: { r: 180, g: 170, b: 140 },
    icon: 'icons/items/Sh_Belt03.png',
    rarity: 'rare',
    weight: 2.5,
    buyPrice: 220,
    stats: { defense: 5, con: 1 },
    slot: 'belt',
    lpcLayer: { layerId: 'belt_leather', variant: 'charcoal' }
  },

  // ── Legs ───────────────────────────────────────────────────

  'leather-greaves': {
    name: 'Leather Greaves',
    type: ITEM_TYPES.ARMOR,
    description: 'Light leg guards made of toughened hide.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/lpc_leather-greaves.png',
    rarity: 'common',
    weight: 2.0,
    buyPrice: 60,
    stats: { defense: 2, end: 1 },
    slot: 'legs',
    lpcLayer: { layerId: 'legs_pants', variant: 'brown' }
  },
  'chain-leggings': {
    name: 'Chain Leggings',
    type: ITEM_TYPES.ARMOR,
    description: 'Flexible mail leggings offering solid defense.',
    color: { r: 150, g: 155, b: 165 },
    icon: 'icons/items/lpc_chain-leggings.png',
    rarity: 'uncommon',
    weight: 3.5,
    buyPrice: 150,
    stats: { defense: 4, con: 1 },
    slot: 'legs',
    lpcLayer: { layerId: 'legs_armour', variant: 'iron' }
  },
  'plate-greaves': {
    name: 'Plate Greaves',
    type: ITEM_TYPES.ARMOR,
    description: 'Heavy plate leg armor for maximum protection.',
    color: { r: 200, g: 200, b: 215 },
    icon: 'icons/items/lpc_plate-greaves.png',
    rarity: 'rare',
    weight: 5.0,
    buyPrice: 300,
    stats: { defense: 7, con: 1 },
    slot: 'legs',
    lpcLayer: { layerId: 'legs_armour', variant: 'steel' }
  },
  'cloth-leggings': {
    name: 'Cloth Leggings',
    type: ITEM_TYPES.ARMOR,
    description: 'Simple cloth leggings. Comfortable and light.',
    color: { r: 100, g: 100, b: 130 },
    icon: 'icons/items/lpc_cloth-leggings.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 25,
    stats: { defense: 1, end: 1 },
    slot: 'legs',
    lpcLayer: { layerId: 'legs_leggings', variant: 'gray' }
  },
  'combat-shorts': {
    name: 'Combat Shorts',
    type: ITEM_TYPES.ARMOR,
    description: 'Short leather breeches that allow freedom of movement.',
    color: { r: 139, g: 90, b: 43 },
    icon: 'icons/items/lpc_combat-shorts.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 30,
    stats: { defense: 1, dex: 1 },
    slot: 'legs',
    lpcLayer: { layerId: 'legs_shorts', variant: 'leather' }
  },
  'battle-skirt': {
    name: 'Battle Skirt',
    type: ITEM_TYPES.ARMOR,
    description: 'A reinforced skirt worn into battle by warriors of all kinds.',
    color: { r: 180, g: 50, b: 50 },
    icon: 'icons/items/lpc_battle-skirt.png',
    rarity: 'uncommon',
    weight: 2.0,
    buyPrice: 120,
    stats: { defense: 3, end: 1 },
    slot: 'legs',
    lpcLayer: { layerId: 'legs_skirts_plain', variant: 'red' }
  },

  // ── Feet ───────────────────────────────────────────────────

  'leather-boots': {
    name: 'Leather Boots',
    type: ITEM_TYPES.ARMOR,
    description: 'Sturdy boots for long journeys.',
    color: { r: 150, g: 110, b: 60 },
    icon: 'icons/items/lpc_leather-boots.png',
    rarity: 'common',
    weight: 1.5,
    buyPrice: 50,
    stats: { defense: 2, end: 1 },
    slot: 'feet',
    lpcLayer: { layerId: 'feet_boots_basic', variant: 'brown' }
  },
  'iron-boots': {
    name: 'Iron Boots',
    type: ITEM_TYPES.ARMOR,
    description: 'Heavy plated boots. Slow but protective.',
    color: { r: 160, g: 165, b: 180 },
    icon: 'icons/items/lpc_iron-boots.png',
    rarity: 'uncommon',
    weight: 3.0,
    buyPrice: 150,
    stats: { defense: 5, con: 1 },
    slot: 'feet',
    lpcLayer: { layerId: 'feet_boots_basic', variant: 'iron' }
  },
  'steel-sabatons': {
    name: 'Steel Sabatons',
    type: ITEM_TYPES.ARMOR,
    description: 'Articulated steel foot armor for heavy combat.',
    color: { r: 190, g: 195, b: 210 },
    icon: 'icons/items/lpc_steel-sabatons.png',
    rarity: 'rare',
    weight: 4.0,
    buyPrice: 280,
    stats: { defense: 7, con: 1 },
    slot: 'feet',
    lpcLayer: { layerId: 'feet_boots_basic', variant: 'steel' }
  },
  'sandals': {
    name: 'Sandals',
    type: ITEM_TYPES.ARMOR,
    description: 'Open footwear suited for warm climates.',
    color: { r: 180, g: 140, b: 80 },
    icon: 'icons/items/lpc_sandals.png',
    rarity: 'common',
    weight: 0.5,
    buyPrice: 15,
    stats: { defense: 1, dex: 1 },
    slot: 'feet',
    lpcLayer: { layerId: 'feet_sandals', variant: 'brown' }
  },
  'cloth-shoes': {
    name: 'Cloth Shoes',
    type: ITEM_TYPES.ARMOR,
    description: 'Basic cloth shoes. At least your feet are covered.',
    color: { r: 100, g: 100, b: 120 },
    icon: 'icons/items/lpc_cloth-shoes.png',
    rarity: 'common',
    weight: 0.5,
    buyPrice: 20,
    stats: { defense: 1, end: 1 },
    slot: 'feet',
    lpcLayer: { layerId: 'feet_shoes_basic', variant: 'gray' }
  },
  'plate-boots': {
    name: 'Plate Boots',
    type: ITEM_TYPES.ARMOR,
    description: 'Heavy plate boots with reinforced toes.',
    color: { r: 180, g: 180, b: 200 },
    icon: 'icons/items/lpc_plate-boots.png',
    rarity: 'rare',
    weight: 4.0,
    buyPrice: 250,
    stats: { defense: 6, con: 1 },
    slot: 'feet',
    lpcLayer: { layerId: 'feet_plate_toe', variant: 'steel' }
  },

  // ── Back (cloaks) ──────────────────────────────────────────

  'tattered-cloak': {
    name: 'Tattered Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'A ragged cloak that has seen better days. Still keeps the chill off.',
    color: { r: 120, g: 100, b: 70 },
    icon: 'icons/items/lpc_tattered-cloak.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 15,
    stats: { defense: 1, end: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_tattered', variant: 'brown' },
      { layerId: 'neck_capeclip', variant: 'brown' }
    ]
  },
  'ragged-cloak': {
    name: 'Ragged Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'A threadbare gray cloak, more holes than cloth.',
    color: { r: 130, g: 130, b: 130 },
    icon: 'icons/items/lpc_tattered-gray.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 15,
    stats: { defense: 1, end: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_tattered', variant: 'gray' },
      { layerId: 'neck_capeclip', variant: 'gray' }
    ]
  },
  'woodsman-cloak': {
    name: "Woodsman's Cloak",
    type: ITEM_TYPES.ARMOR,
    description: 'A tattered green cloak favored by those who live off the land.',
    color: { r: 80, g: 130, b: 60 },
    icon: 'icons/items/lpc_tattered-green.png',
    rarity: 'common',
    weight: 1.0,
    buyPrice: 20,
    stats: { defense: 1, end: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_tattered', variant: 'green' },
      { layerId: 'neck_capeclip', variant: 'green' }
    ]
  },
  'traveler-cloak': {
    name: "Traveler's Cloak",
    type: ITEM_TYPES.ARMOR,
    description: 'A weathered cloak offering modest protection.',
    color: { r: 160, g: 130, b: 90 },
    icon: 'icons/items/lpc_traveler-cloak.png',
    rarity: 'common',
    weight: 1.5,
    buyPrice: 40,
    stats: { defense: 2, end: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'brown' },
      { layerId: 'neck_capeclip', variant: 'brown' }
    ]
  },
  'green-cloak': {
    name: 'Green Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'A vivid green cloak that blends with foliage.',
    color: { r: 60, g: 160, b: 60 },
    icon: 'icons/items/lpc_green-cloak.png',
    rarity: 'uncommon',
    weight: 1.5,
    buyPrice: 100,
    stats: { defense: 3, dex: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'green' },
      { layerId: 'neck_capeclip', variant: 'green' }
    ]
  },
  'white-cloak': {
    name: 'White Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'A pristine white cloak that billows dramatically.',
    color: { r: 230, g: 230, b: 240 },
    icon: 'icons/items/lpc_white-cloak.png',
    rarity: 'uncommon',
    weight: 1.5,
    buyPrice: 120,
    stats: { defense: 3, wis: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'white' },
      { layerId: 'neck_capeclip', variant: 'white' }
    ]
  },
  'maroon-cloak': {
    name: 'Maroon Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'A deep maroon cloak with a rich, distinguished look.',
    color: { r: 128, g: 30, b: 30 },
    icon: 'icons/items/lpc_maroon-cloak.png',
    rarity: 'uncommon',
    weight: 1.5,
    buyPrice: 130,
    stats: { defense: 3, attackPower: 1, str: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'maroon' },
      { layerId: 'neck_capeclip', variant: 'maroon' }
    ]
  },
  'mage-cloak': {
    name: "Mage's Cloak",
    type: ITEM_TYPES.ARMOR,
    description: 'A flowing cloak woven with protective wards.',
    color: { r: 100, g: 80, b: 180 },
    icon: 'icons/items/lpc_mage-cloak.png',
    rarity: 'uncommon',
    weight: 1.5,
    buyPrice: 140,
    stats: { defense: 3, attackPower: 2, int: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'blue' },
      { layerId: 'neck_capeclip', variant: 'blue' }
    ]
  },
  'shadow-cloak': {
    name: 'Shadow Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'A cloak spun from midnight threads. Hides and protects.',
    color: { r: 60, g: 50, b: 80 },
    icon: 'icons/items/lpc_shadow-cloak.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 300,
    stats: { defense: 5, attackPower: 3, dex: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'gray' },
      { layerId: 'neck_capeclip', variant: 'gray' }
    ]
  },
  'navy-cape': {
    name: 'Navy Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'A trimmed navy cape worn by seasoned officers.',
    color: { r: 40, g: 40, b: 100 },
    icon: 'icons/items/lpc_navy-cape.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 280,
    stats: { defense: 5, attackPower: 2, con: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'navy' },
      { layerId: 'cape_trim', variant: 'orange' },
      { layerId: 'neck_capetie', variant: 'navy' }
    ]
  },
  'forest-cape': {
    name: 'Forest Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'A trimmed cape dyed with forest herbs. Rangers swear by it.',
    color: { r: 40, g: 100, b: 40 },
    icon: 'icons/items/lpc_forest-cape.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 300,
    stats: { defense: 5, attackPower: 3, dex: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'forest' },
      { layerId: 'cape_trim', variant: 'brown' },
      { layerId: 'neck_capetie', variant: 'forest' }
    ]
  },
  'frost-cape': {
    name: 'Frost Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'A pale blue cape trimmed with icy threads. Cold to the touch.',
    color: { r: 160, g: 200, b: 240 },
    icon: 'icons/items/lpc_frost-cape.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 320,
    stats: { defense: 5, attackPower: 3, int: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'sky' },
      { layerId: 'cape_trim', variant: 'white' },
      { layerId: 'neck_capetie', variant: 'sky' }
    ]
  },
  'royal-cape': {
    name: 'Royal Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'A resplendent trimmed cape worthy of royalty.',
    color: { r: 180, g: 50, b: 50 },
    icon: 'icons/items/lpc_royal-cape.png',
    rarity: 'epic',
    weight: 2.0,
    buyPrice: 400,
    stats: { defense: 6, attackPower: 4, str: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'red' },
      { layerId: 'cape_trim', variant: 'orange' },
      { layerId: 'neck_capetie', variant: 'red' }
    ]
  },
  'purple-cape': {
    name: 'Purple Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'A majestic purple cape with ornate trim. Fit for an archmage.',
    color: { r: 120, g: 50, b: 160 },
    icon: 'icons/items/lpc_purple-cape.png',
    rarity: 'epic',
    weight: 2.0,
    buyPrice: 420,
    stats: { defense: 6, attackPower: 5, int: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'purple' },
      { layerId: 'cape_trim', variant: 'white' },
      { layerId: 'neck_capetie', variant: 'purple' }
    ]
  },
  'golden-cape': {
    name: 'Golden Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'A blazing orange-gold cape that shimmers in torchlight.',
    color: { r: 220, g: 160, b: 40 },
    icon: 'icons/items/lpc_golden-cape.png',
    rarity: 'epic',
    weight: 2.0,
    buyPrice: 450,
    stats: { defense: 7, attackPower: 4, con: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'orange' },
      { layerId: 'cape_trim', variant: 'red' },
      { layerId: 'neck_capetie', variant: 'orange' }
    ]
  },
  'rose-cape': {
    name: 'Rose Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'A delicate rose-colored cape with a subtle floral trim.',
    color: { r: 200, g: 120, b: 140 },
    icon: 'icons/items/lpc_rose-cape.png',
    rarity: 'epic',
    weight: 1.8,
    buyPrice: 400,
    stats: { defense: 6, attackPower: 4, wis: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'rose' },
      { layerId: 'cape_trim', variant: 'white' },
      { layerId: 'neck_capetie', variant: 'rose' }
    ]
  },
  'crimson-mantle': {
    name: 'Crimson Mantle',
    type: ITEM_TYPES.ARMOR,
    description: 'A blood-red cape edged with gold thread. Worn by war heroes.',
    color: { r: 180, g: 30, b: 30 },
    icon: 'icons/items/lpc_crimson-mantle.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 350,
    stats: { defense: 5, attackPower: 4, str: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'maroon' },
      { layerId: 'cape_trim', variant: 'orange' },
      { layerId: 'neck_capetie', variant: 'maroon' }
    ]
  },
  'midnight-cape': {
    name: 'Midnight Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'Black as a moonless night with a sinister violet edge.',
    color: { r: 20, g: 15, b: 40 },
    icon: 'icons/items/lpc_midnight-cape.png',
    rarity: 'epic',
    weight: 1.8,
    buyPrice: 480,
    stats: { defense: 7, attackPower: 5, dex: 2 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'black' },
      { layerId: 'cape_trim', variant: 'purple' },
      { layerId: 'neck_capetie', variant: 'black' }
    ]
  },
  'healers-mantle': {
    name: "Healer's Mantle",
    type: ITEM_TYPES.ARMOR,
    description: 'A soft lavender cape with white silk trim. Soothes the weary.',
    color: { r: 180, g: 160, b: 220 },
    icon: 'icons/items/lpc_healers-mantle.png',
    rarity: 'rare',
    weight: 1.5,
    buyPrice: 320,
    stats: { defense: 4, attackPower: 3, wis: 2 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'lavender' },
      { layerId: 'cape_trim', variant: 'white' },
      { layerId: 'neck_capetie', variant: 'lavender' }
    ]
  },
  'seafarer-cape': {
    name: "Seafarer's Cape",
    type: ITEM_TYPES.ARMOR,
    description: 'A teal cape with gold braid trim. Smells faintly of salt.',
    color: { r: 60, g: 160, b: 150 },
    icon: 'icons/items/lpc_seafarer-cape.png',
    rarity: 'rare',
    weight: 2.0,
    buyPrice: 340,
    stats: { defense: 5, attackPower: 3, end: 2 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'teal' },
      { layerId: 'cape_trim', variant: 'orange' },
      { layerId: 'neck_capetie', variant: 'teal' }
    ]
  },
  'ember-cloak': {
    name: 'Ember Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'Woven from fireweave cloth. The red trim seems to flicker.',
    color: { r: 200, g: 100, b: 30 },
    icon: 'icons/items/lpc_ember-cloak.png',
    rarity: 'epic',
    weight: 1.8,
    buyPrice: 460,
    stats: { defense: 7, attackPower: 6, int: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'orange' },
      { layerId: 'cape_trim', variant: 'red' },
      { layerId: 'neck_capetie', variant: 'red' }
    ]
  },
  'ivory-cape': {
    name: 'Ivory Cape',
    type: ITEM_TYPES.ARMOR,
    description: 'Pristine white silk bordered with sky-blue thread. Worn by paladins.',
    color: { r: 240, g: 240, b: 250 },
    icon: 'icons/items/lpc_ivory-cape.png',
    rarity: 'epic',
    weight: 1.8,
    buyPrice: 440,
    stats: { defense: 7, attackPower: 4, wis: 1, con: 1 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'white' },
      { layerId: 'cape_trim', variant: 'sky' },
      { layerId: 'neck_capetie', variant: 'white' }
    ]
  },

  // ── Off Hand (shields) ─────────────────────────────────────

  'wooden-shield': {
    name: 'Wooden Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A round wooden shield banded with iron.',
    color: { r: 139, g: 100, b: 60 },
    icon: 'icons/items/lpc_wooden-shield.png',
    rarity: 'common',
    weight: 3.0,
    buyPrice: 70,
    stats: { defense: 4, con: 1 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'brown' },
      { layerId: 'shield_heater_trim', variant: 'iron' }
    ]
  },
  'iron-shield': {
    name: 'Iron Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A solid iron kite shield with a pale stripe.',
    color: { r: 160, g: 165, b: 175 },
    icon: 'icons/items/lpc_iron-shield.png',
    rarity: 'uncommon',
    weight: 4.0,
    buyPrice: 180,
    stats: { defense: 7, con: 1 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'gray' },
      { layerId: 'shield_heater_pattern_pale', variant: 'white' },
      { layerId: 'shield_heater_trim', variant: 'steel' }
    ]
  },
  'emerald-shield': {
    name: 'Emerald Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A shield painted in deep emerald green with a golden chevron.',
    color: { r: 80, g: 180, b: 80 },
    icon: 'icons/items/lpc_emerald-shield.png',
    rarity: 'uncommon',
    weight: 4.0,
    buyPrice: 200,
    stats: { defense: 7, con: 1 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'emerald' },
      { layerId: 'shield_heater_pattern_chevron', variant: 'yellow' },
      { layerId: 'shield_heater_trim', variant: 'copper' }
    ]
  },
  'crimson-shield': {
    name: 'Crimson Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A fierce red shield with a diagonal bend, trimmed with brass.',
    color: { r: 180, g: 50, b: 50 },
    icon: 'icons/items/lpc_crimson-shield.png',
    rarity: 'uncommon',
    weight: 4.0,
    buyPrice: 200,
    stats: { defense: 7, con: 1 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'red' },
      { layerId: 'shield_heater_pattern_bend', variant: 'yellow' },
      { layerId: 'shield_heater_trim', variant: 'brass' }
    ]
  },
  'midnight-shield': {
    name: 'Midnight Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A dark shield with a silver saltire, forged under moonless skies.',
    color: { r: 30, g: 30, b: 60 },
    icon: 'icons/items/lpc_midnight-shield.png',
    rarity: 'rare',
    weight: 4.5,
    buyPrice: 280,
    stats: { defense: 8, con: 1 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'midnight' },
      { layerId: 'shield_heater_pattern_saltire', variant: 'slate' },
      { layerId: 'shield_heater_trim', variant: 'silver' }
    ]
  },
  'tower-shield': {
    name: 'Tower Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A massive shield quartered in blue and white.',
    color: { r: 190, g: 195, b: 210 },
    icon: 'icons/items/lpc_tower-shield.png',
    rarity: 'rare',
    weight: 5.0,
    buyPrice: 350,
    stats: { defense: 10, con: 2 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'blue' },
      { layerId: 'shield_heater_pattern_quarterly', variant: 'white' },
      { layerId: 'shield_heater_trim', variant: 'silver' }
    ]
  },
  'spartan-shield': {
    name: 'Spartan Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A large shield with an inverted chevron in the Spartan tradition.',
    color: { r: 180, g: 50, b: 50 },
    icon: 'icons/items/lpc_spartan-shield.png',
    rarity: 'rare',
    weight: 5.0,
    buyPrice: 300,
    stats: { defense: 9, con: 2 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'red' },
      { layerId: 'shield_heater_pattern_chevron_inverted', variant: 'black' },
      { layerId: 'shield_heater_trim', variant: 'gold' }
    ]
  },
  'royal-shield': {
    name: 'Royal Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A regal purple shield with a golden cross, favored by kings.',
    color: { r: 120, g: 60, b: 180 },
    icon: 'icons/items/lpc_royal-shield.png',
    rarity: 'rare',
    weight: 5.0,
    buyPrice: 380,
    stats: { defense: 10, con: 2 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'purple' },
      { layerId: 'shield_heater_pattern_cross', variant: 'yellow' },
      { layerId: 'shield_heater_trim', variant: 'gold' }
    ]
  },
  'forest-shield': {
    name: 'Forest Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A deep green shield split per fess, worn by woodland rangers.',
    color: { r: 50, g: 120, b: 50 },
    icon: 'icons/items/lpc_forest-shield.png',
    rarity: 'rare',
    weight: 4.5,
    buyPrice: 320,
    stats: { defense: 9, con: 1 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'forest' },
      { layerId: 'shield_heater_pattern_per_fess', variant: 'brown' },
      { layerId: 'shield_heater_trim', variant: 'bronze' }
    ]
  },
  'crusader-shield': {
    name: 'Crusader Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A white shield emblazoned with the red cross of the crusaders.',
    color: { r: 200, g: 200, b: 215 },
    icon: 'icons/items/lpc_crusader-shield.png',
    rarity: 'epic',
    weight: 6.0,
    buyPrice: 500,
    stats: { defense: 12, con: 2 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'white' },
      { layerId: 'shield_heater_pattern_cross', variant: 'red' },
      { layerId: 'shield_heater_trim', variant: 'silver' }
    ]
  },
  'golden-shield': {
    name: 'Golden Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'A magnificent golden shield with a lozengy pattern.',
    color: { r: 220, g: 190, b: 50 },
    icon: 'icons/items/lpc_golden-shield.png',
    rarity: 'epic',
    weight: 6.0,
    buyPrice: 550,
    stats: { defense: 13, con: 2 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'honey' },
      { layerId: 'shield_heater_pattern_lozengy', variant: 'yellow' },
      { layerId: 'shield_heater_trim', variant: 'gold' }
    ]
  },
  'shadow-shield': {
    name: 'Shadow Shield',
    type: ITEM_TYPES.ARMOR,
    description: 'An obsidian shield split per pale, seeming to drink in the light.',
    color: { r: 40, g: 35, b: 50 },
    icon: 'icons/items/lpc_shadow-shield.png',
    rarity: 'epic',
    weight: 5.5,
    buyPrice: 520,
    stats: { defense: 12, attackPower: 2, con: 1 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'shadow' },
      { layerId: 'shield_heater_pattern_per_pale', variant: 'charcoal' },
      { layerId: 'shield_heater_trim', variant: 'steel' }
    ]
  },

  // ── Neck ───────────────────────────────────────────────────

  'iron-amulet': {
    name: 'Iron Amulet',
    type: ITEM_TYPES.ARMOR,
    description: 'A simple iron pendant on a leather cord.',
    color: { r: 160, g: 165, b: 175 },
    icon: 'icons/items/Ac_Necklace02.png',
    rarity: 'common',
    weight: 0.3,
    buyPrice: 60,
    stats: { defense: 2, con: 1 },
    slot: 'neck'
  },
  'ruby-necklace': {
    name: 'Ruby Necklace',
    type: ITEM_TYPES.ARMOR,
    description: 'An exquisite necklace set with a deep red ruby.',
    color: { r: 200, g: 50, b: 50 },
    icon: 'icons/items/Ac_Necklace01.png',
    rarity: 'rare',
    weight: 0.3,
    buyPrice: 260,
    stats: { attackPower: 4, defense: 2, str: 1 },
    slot: 'neck'
  },
  'sapphire-pendant': {
    name: 'Sapphire Pendant',
    type: ITEM_TYPES.ARMOR,
    description: 'A shimmering sapphire pendant that bolsters its wearer.',
    color: { r: 40, g: 80, b: 220 },
    icon: 'icons/items/Ac_Necklace05.png',
    rarity: 'epic',
    weight: 0.3,
    buyPrice: 500,
    stats: { attackPower: 5, defense: 4, int: 1 },
    slot: 'neck'
  },

  // ── Rings ──────────────────────────────────────────────────

  'silver-ring': {
    name: 'Silver Ring',
    type: ITEM_TYPES.ARMOR,
    description: 'A simple silver band with a faint enchantment.',
    color: { r: 180, g: 185, b: 195 },
    icon: 'icons/items/Ac_Ring01.png',
    rarity: 'common',
    weight: 0.2,
    buyPrice: 45,
    stats: { defense: 1, con: 1 },
    slot: 'ring'
  },
  'gold-ring': {
    name: 'Gold Ring',
    type: ITEM_TYPES.ARMOR,
    description: 'A gold ring inscribed with runes of strength.',
    color: { r: 255, g: 215, b: 0 },
    icon: 'icons/items/Ac_Ring02.png',
    rarity: 'uncommon',
    weight: 0.2,
    buyPrice: 120,
    stats: { attackPower: 2, str: 1 },
    slot: 'ring'
  },
  'emerald-ring': {
    name: 'Emerald Ring',
    type: ITEM_TYPES.ARMOR,
    description: 'A ring set with a brilliant emerald. Radiates protection.',
    color: { r: 40, g: 180, b: 80 },
    icon: 'icons/items/Ac_Ring01.png',
    rarity: 'rare',
    weight: 0.2,
    buyPrice: 250,
    stats: { defense: 3, attackPower: 1, wis: 1 },
    slot: 'ring'
  },
  'ring-of-power': {
    name: 'Ring of Power',
    type: ITEM_TYPES.ARMOR,
    description: 'An ancient ring pulsing with immense magical energy.',
    color: { r: 163, g: 53, b: 238 },
    icon: 'icons/items/Ac_Ring02.png',
    rarity: 'epic',
    weight: 0.2,
    buyPrice: 600,
    stats: { attackPower: 5, defense: 3, int: 1 },
    slot: 'ring'
  },

  // ── Gems & Valuables (sell-only treasures) ───────────────

  'ruby': {
    name: 'Ruby',
    type: ITEM_TYPES.TREASURE,
    description: 'A brilliant red gemstone. Worth a fair sum.',
    color: { r: 220, g: 40, b: 40 },
    icon: 'icons/items/I_Ruby.png',
    rarity: 'uncommon',
    sellPrice: 75,
    stackable: true,
    weight: 0.5
  },
  'sapphire': {
    name: 'Sapphire',
    type: ITEM_TYPES.TREASURE,
    description: 'A deep blue gem prized by collectors.',
    color: { r: 40, g: 80, b: 220 },
    icon: 'icons/items/I_Sapphire.png',
    rarity: 'rare',
    sellPrice: 100,
    stackable: true,
    weight: 0.5
  },
  'emerald': {
    name: 'Emerald',
    type: ITEM_TYPES.TREASURE,
    description: 'A vivid green gem from deep within the earth.',
    color: { r: 40, g: 180, b: 80 },
    icon: 'icons/items/I_Jade.png',
    rarity: 'uncommon',
    sellPrice: 80,
    stackable: true,
    weight: 0.5
  },
  'diamond': {
    name: 'Diamond',
    type: ITEM_TYPES.TREASURE,
    description: 'The hardest and most valuable of all gemstones.',
    color: { r: 200, g: 220, b: 240 },
    icon: 'icons/items/I_Diamond.png',
    rarity: 'epic',
    sellPrice: 200,
    stackable: true,
    weight: 0.5
  },
  'amethyst': {
    name: 'Amethyst',
    type: ITEM_TYPES.TREASURE,
    description: 'A purple crystal humming with faint energy.',
    color: { r: 160, g: 60, b: 220 },
    icon: 'icons/items/I_Amethist.png',
    rarity: 'uncommon',
    sellPrice: 60,
    stackable: true,
    weight: 0.5
  },
  'opal': {
    name: 'Opal',
    type: ITEM_TYPES.TREASURE,
    description: 'An iridescent stone that shifts colors in light.',
    color: { r: 180, g: 200, b: 180 },
    icon: 'icons/items/I_Opal.png',
    rarity: 'rare',
    sellPrice: 90,
    stackable: true,
    weight: 0.5
  },
  'agate': {
    name: 'Agate',
    type: ITEM_TYPES.TREASURE,
    description: 'A smooth, banded stone. Modestly valuable.',
    color: { r: 180, g: 60, b: 100 },
    icon: 'icons/items/I_Agate.png',
    rarity: 'common',
    sellPrice: 40,
    stackable: true,
    weight: 0.5
  },
  'gold-bar': {
    name: 'Gold Bar',
    type: ITEM_TYPES.TREASURE,
    description: 'A heavy bar of pure gold. Very valuable.',
    color: { r: 255, g: 215, b: 0 },
    icon: 'icons/items/I_GoldBar.png',
    rarity: 'rare',
    sellPrice: 150,
    stackable: true,
    weight: 2.0
  },
  'silver-coin': {
    name: 'Silver Coin',
    type: ITEM_TYPES.TREASURE,
    description: 'An old silver coin. Still has value.',
    color: { r: 180, g: 185, b: 195 },
    icon: 'icons/items/I_SilverCoin.png',
    rarity: 'common',
    sellPrice: 25,
    stackable: true,
    weight: 0.1
  },
  'gold-nugget': {
    name: 'Gold Nugget',
    type: ITEM_TYPES.TREASURE,
    description: 'A rough lump of gold ore.',
    color: { r: 230, g: 190, b: 40 },
    icon: 'icons/items/E_Gold02.png',
    rarity: 'uncommon',
    sellPrice: 50,
    stackable: true,
    weight: 1.0
  },

  // ── Keys & Quest Items ───────────────────────────────────

  'bronze-key': {
    name: 'Bronze Key',
    type: ITEM_TYPES.KEY,
    description: 'A weathered bronze key. Opens basic keyed dungeons.',
    color: { r: 205, g: 127, b: 50 },
    icon: 'icons/items/I_Key03.png',
    rarity: 'common',
    keyTier: 'bronze',
    unique: false,
    stackable: true,
    weight: 0.1
  },
  'dungeon-key': {
    name: 'Bronze Key',
    type: ITEM_TYPES.KEY,
    description: 'A weathered bronze key. Opens basic keyed dungeons.',
    color: { r: 205, g: 127, b: 50 },
    icon: 'icons/items/I_Key03.png',
    rarity: 'common',
    keyTier: 'bronze',
    unique: false,
    stackable: true,
    weight: 0.1
  },
  'silver-key': {
    name: 'Silver Key',
    type: ITEM_TYPES.KEY,
    description: 'A polished silver key. Opens silver keyed dungeons with better loot.',
    color: { r: 192, g: 192, b: 192 },
    icon: 'icons/items/I_Key02.png',
    rarity: 'uncommon',
    keyTier: 'silver',
    unique: false,
    stackable: true,
    weight: 0.1
  },
  'gold-key': {
    name: 'Gold Key',
    type: ITEM_TYPES.KEY,
    description: 'A radiant golden key. Opens gold keyed dungeons with the best loot.',
    color: { r: 255, g: 215, b: 0 },
    icon: 'icons/items/I_Key05.png',
    rarity: 'rare',
    keyTier: 'gold',
    unique: false,
    stackable: true,
    weight: 0.1
  },
  'master-key': {
    name: 'Gold Key',
    type: ITEM_TYPES.KEY,
    description: 'A radiant golden key. Opens gold keyed dungeons with the best loot.',
    color: { r: 255, g: 215, b: 0 },
    icon: 'icons/items/I_Key05.png',
    rarity: 'rare',
    keyTier: 'gold',
    unique: false,
    stackable: true,
    weight: 0.1
  },
  'ancient-relic': {
    name: 'Ancient Relic',
    type: ITEM_TYPES.QUEST,
    description: 'A mysterious crystalline artifact pulsing with ancient power.',
    color: { r: 128, g: 0, b: 255 },
    icon: 'icons/items/I_Crystal01.png',
    rarity: 'legendary',
    unique: true,
    weight: 0.5
  },
  'magic-scroll': {
    name: 'Magic Scroll',
    type: ITEM_TYPES.QUEST,
    description: 'A weathered scroll covered in arcane writing.',
    color: { r: 200, g: 170, b: 120 },
    icon: 'icons/items/I_Scroll.png',
    rarity: 'rare',
    unique: true,
    weight: 0.2
  },
  'old-book': {
    name: 'Old Book',
    type: ITEM_TYPES.QUEST,
    description: 'A dusty tome of forgotten knowledge.',
    color: { r: 160, g: 175, b: 200 },
    icon: 'icons/items/I_Book.png',
    rarity: 'uncommon',
    unique: true,
    weight: 0.5
  },
  'gold-medal': {
    name: 'Gold Medal',
    type: ITEM_TYPES.TREASURE,
    description: 'A gleaming medal awarded to champions.',
    color: { r: 255, g: 200, b: 50 },
    icon: 'icons/items/Ac_Medal01.png',
    rarity: 'rare',
    sellPrice: 120,
    stackable: false,
    weight: 0.5
  },

  // ── Mystery Rewards (key trade exclusives) ──────────────

  'hellfire-blade': {
    name: 'Hellfire Blade',
    type: ITEM_TYPES.WEAPON,
    description: 'Forged in the deepest hellrat den. The hellrats were NOT consulted.',
    color: { r: 255, g: 80, b: 20 },
    icon: 'icons/items/W_Sword018.png',
    rarity: 'legendary',
    weight: 4.5,
    stats: { attackPower: 22, str: 5 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_glowsword', variant: 'red' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'abyssal-crown': {
    name: 'Abyssal Crown',
    type: ITEM_TYPES.ARMOR,
    description: 'A crown from the abyss. Comes with free existential dread.',
    color: { r: 80, g: 0, b: 160 },
    icon: 'icons/items/lpc_abyssal-crown.png',
    rarity: 'legendary',
    weight: 3.0,
    stats: { defense: 12, attackPower: 8, int: 3 },
    slot: 'head',
    lpcLayer: { layerId: 'hat_helmet_mail', variant: 'gold' }
  },
  'dungeon-slayer': {
    name: 'Dungeon Slayer',
    type: ITEM_TYPES.WEAPON,
    description: "This sword has cleared more dungeons than you've had hot dinners.",
    color: { r: 180, g: 50, b: 50 },
    icon: 'icons/items/W_Sword009.png',
    rarity: 'epic',
    weight: 5.0,
    stats: { attackPower: 18, str: 4 },
    slot: 'mainhand',
    lpcLayer: { layerId: 'weapon_sword_saber', variant: 'saber' },
    attackAnims: ['weapon-slash', 'weapon-slash']
  },
  'keymasters-ward': {
    name: "Keymaster's Ward",
    type: ITEM_TYPES.ARMOR,
    description: 'A shield forged from the souls of sacrificed keys. They died for this.',
    color: { r: 200, g: 180, b: 50 },
    icon: 'icons/items/lpc_keymasters-ward.png',
    rarity: 'epic',
    weight: 4.5,
    stats: { defense: 12, attackPower: 3, con: 3 },
    slot: 'offhand',
    lpcLayer: [
      { layerId: 'shield_heater_paint', variant: 'purple' },
      { layerId: 'shield_heater_pattern_pall', variant: 'yellow' },
      { layerId: 'shield_heater_trim', variant: 'gold' }
    ]
  },
  'void-cloak': {
    name: 'Void Cloak',
    type: ITEM_TYPES.ARMOR,
    description: 'Woven from pure darkness. Dry clean only.',
    color: { r: 30, g: 10, b: 60 },
    icon: 'icons/items/lpc_void-cloak.png',
    rarity: 'epic',
    weight: 1.5,
    stats: { defense: 8, attackPower: 6, dex: 2 },
    slot: 'back',
    lpcLayer: [
      { layerId: 'cape_solid', variant: 'charcoal' },
      { layerId: 'cape_trim', variant: 'purple' },
      { layerId: 'neck_capetie', variant: 'charcoal' }
    ]
  },
  'ring-of-the-depths': {
    name: 'Ring of the Depths',
    type: ITEM_TYPES.ARMOR,
    description: 'It whispers dungeon secrets. Mostly about hellrats.',
    color: { r: 20, g: 200, b: 200 },
    icon: 'icons/items/Ac_Ring02.png',
    rarity: 'epic',
    weight: 0.2,
    stats: { attackPower: 7, defense: 5, int: 2 },
    slot: 'ring'
  },
};

/**
 * Get the sell price for an item.
 * Treasures use explicit sellPrice; equipment/consumables sell at 50% of buyPrice.
 */
export function getSellPrice(itemId) {
  const def = ITEM_DEFS[itemId];
  if (!def) return 0;
  if (def.sellPrice != null) return def.sellPrice;
  return def.buyPrice ? Math.floor(def.buyPrice * 0.5) : 0;
}

/**
 * Merchant stock — items available for purchase.
 */
export const MERCHANT_STOCK = [
  // Consumables
  { itemId: 'health-potion' },
  { itemId: 'large-health-potion' },
  { itemId: 'mana-potion' },
  { itemId: 'large-mana-potion' },
  { itemId: 'stamina-potion' },
  { itemId: 'large-stamina-potion' },
  { itemId: 'antidote' },
  // Weapons — melee
  { itemId: 'iron-sword' },
  { itemId: 'steel-blade' },
  { itemId: 'iron-dagger' },
  { itemId: 'war-axe' },
  { itemId: 'iron-mace' },
  { itemId: 'iron-spear' },
  { itemId: 'flail' },
  { itemId: 'scimitar' },
  // Weapons — ranged
  { itemId: 'hunter-bow' },
  { itemId: 'recurve-bow' },
  { itemId: 'crossbow' },
  { itemId: 'boomerang' },
  { itemId: 'slingshot' },
  // Weapons — magic
  { itemId: 'magic-wand' },
  { itemId: 'fire-staff' },
  { itemId: 'gnarled-staff' },
  { itemId: 'loop-staff' },
  { itemId: 'serpent-staff' },
  { itemId: 'walking-cane' },
  // Armor — head
  { itemId: 'cloth-hood' },
  { itemId: 'iron-helm' },
  { itemId: 'kettle-helm' },
  // Armor — body
  { itemId: 'tunic' },
  { itemId: 'leather-armor' },
  { itemId: 'chain-mail' },
  { itemId: 'mage-robe' },
  { itemId: 'tabard' },
  // Armor — shoulders
  { itemId: 'leather-shoulders' },
  { itemId: 'iron-pauldrons' },
  // Armor — hands
  { itemId: 'leather-gloves' },
  { itemId: 'iron-bracers' },
  // Armor — belt
  { itemId: 'leather-belt' },
  // Armor — legs
  { itemId: 'cloth-leggings' },
  { itemId: 'leather-greaves' },
  { itemId: 'combat-shorts' },
  // Armor — feet
  { itemId: 'sandals' },
  { itemId: 'cloth-shoes' },
  { itemId: 'leather-boots' },
  // Armor — back
  { itemId: 'tattered-cloak' },
  { itemId: 'traveler-cloak' },
  { itemId: 'green-cloak' },
  { itemId: 'maroon-cloak' },
  // Armor — off hand
  { itemId: 'wooden-shield' },
  // Jewelry
  { itemId: 'iron-amulet' },
  { itemId: 'silver-ring' },
];

/**
 * Gold drop ranges per enemy loot table tier.
 */
export const GOLD_DROP_TABLES = {
  default: { min: 5, max: 15 },
  elite:   { min: 20, max: 50 },
  boss:    { min: 80, max: 150 },
  dungeon_bronze: { min: 15, max: 40 },
  dungeon_silver: { min: 35, max: 80 },
  dungeon_gold:   { min: 60, max: 150 },
};

/**
 * Dungeon key tier configuration.
 * lootMul: multiplier for drop chances in keyed dungeons.
 * bonusRooms: extra rooms generated in keyed dungeons.
 */
export const KEY_TIERS = {
  bronze: { lootMul: 1.2, color: '#cd7f32', bonusRooms: 0, lootTable: 'dungeon_bronze' },
  silver: { lootMul: 1.5, color: '#c0c0c0', bonusRooms: 2, lootTable: 'dungeon_silver' },
  gold:   { lootMul: 2.0, color: '#ffd700', bonusRooms: 4, lootTable: 'dungeon_gold' }
};

/**
 * Loot tables: arrays of { itemId, chance }.
 * Each entry is rolled independently.
 */
export const LOOT_TABLES = {
  default: [
    { itemId: 'health-potion', chance: 0.4 },
    { itemId: 'mana-potion', chance: 0.15 },
    { itemId: 'stamina-potion', chance: 0.15 },
    { itemId: 'agate', chance: 0.1 },
    { itemId: 'silver-coin', chance: 0.15 },
    { itemId: 'leather-belt', chance: 0.05 },
    { itemId: 'silver-ring', chance: 0.04 },
    { itemId: 'cloth-shoes', chance: 0.04 },
    { itemId: 'cloth-leggings', chance: 0.04 },
    { itemId: 'tattered-cloak', chance: 0.03 },
    { itemId: 'ragged-cloak', chance: 0.02 },
    { itemId: 'woodsman-cloak', chance: 0.02 },
    { itemId: 'slingshot', chance: 0.03 },
    { itemId: 'bronze-key', chance: 0.03 },
  ],
  elite: [
    { itemId: 'health-potion', chance: 0.6 },
    { itemId: 'mana-potion', chance: 0.25 },
    { itemId: 'stamina-potion', chance: 0.25 },
    { itemId: 'iron-sword', chance: 0.12 },
    { itemId: 'scimitar', chance: 0.06 },
    { itemId: 'flail', chance: 0.06 },
    { itemId: 'crossbow', chance: 0.05 },
    { itemId: 'leather-armor', chance: 0.08 },
    { itemId: 'iron-helm', chance: 0.06 },
    { itemId: 'iron-pauldrons', chance: 0.06 },
    { itemId: 'leather-shoulders', chance: 0.07 },
    { itemId: 'leather-gloves', chance: 0.07 },
    { itemId: 'iron-bracers', chance: 0.05 },
    { itemId: 'leather-greaves', chance: 0.07 },
    { itemId: 'leather-boots', chance: 0.07 },
    { itemId: 'wooden-shield', chance: 0.06 },
    { itemId: 'crimson-shield', chance: 0.04 },
    { itemId: 'emerald-shield', chance: 0.04 },
    { itemId: 'iron-amulet', chance: 0.05 },
    { itemId: 'gold-ring', chance: 0.04 },
    { itemId: 'ruby', chance: 0.12 },
    { itemId: 'amethyst', chance: 0.1 },
    { itemId: 'gold-nugget', chance: 0.15 },
    { itemId: 'gnarled-staff', chance: 0.04 },
    { itemId: 'green-cloak', chance: 0.04 },
    { itemId: 'white-cloak', chance: 0.03 },
    { itemId: 'maroon-cloak', chance: 0.03 },
    { itemId: 'tabard', chance: 0.05 },
    { itemId: 'battle-skirt', chance: 0.04 },
    { itemId: 'silver-key', chance: 0.08 },
  ],
  boss: [
    { itemId: 'large-health-potion', chance: 0.8 },
    { itemId: 'large-mana-potion', chance: 0.5 },
    { itemId: 'large-stamina-potion', chance: 0.5 },
    { itemId: 'fire-staff', chance: 0.3 },
    { itemId: 'crystal-staff', chance: 0.15 },
    { itemId: 'katana', chance: 0.12 },
    { itemId: 'halberd', chance: 0.1 },
    { itemId: 'trident', chance: 0.1 },
    { itemId: 'great-bow', chance: 0.1 },
    { itemId: 'chain-mail', chance: 0.2 },
    { itemId: 'legion-armor', chance: 0.1 },
    { itemId: 'knight-helm', chance: 0.15 },
    { itemId: 'barbarian-helm', chance: 0.1 },
    { itemId: 'steel-spaulders', chance: 0.15 },
    { itemId: 'legion-shoulders', chance: 0.08 },
    { itemId: 'chain-gauntlets', chance: 0.15 },
    { itemId: 'chain-leggings', chance: 0.15 },
    { itemId: 'plate-greaves', chance: 0.08 },
    { itemId: 'iron-boots', chance: 0.15 },
    { itemId: 'plate-boots', chance: 0.08 },
    { itemId: 'iron-shield', chance: 0.12 },
    { itemId: 'midnight-shield', chance: 0.06 },
    { itemId: 'spartan-shield', chance: 0.06 },
    { itemId: 'royal-shield', chance: 0.04 },
    { itemId: 'forest-shield', chance: 0.05 },
    { itemId: 'mage-cloak', chance: 0.1 },
    { itemId: 'shadow-cloak', chance: 0.06 },
    { itemId: 'navy-cape', chance: 0.05 },
    { itemId: 'forest-cape', chance: 0.05 },
    { itemId: 'frost-cape', chance: 0.04 },
    { itemId: 'royal-cape', chance: 0.05 },
    { itemId: 'purple-cape', chance: 0.03 },
    { itemId: 'golden-cape', chance: 0.03 },
    { itemId: 'ruby-necklace', chance: 0.08 },
    { itemId: 'emerald-ring', chance: 0.08 },
    { itemId: 'reinforced-belt', chance: 0.1 },
    { itemId: 'gold-key', chance: 1.0 },
    { itemId: 'sapphire', chance: 0.4 },
    { itemId: 'diamond', chance: 0.15 },
    { itemId: 'gold-bar', chance: 0.3 },
  ],

  // Keyed dungeon loot tables (used inside keyed dungeons, not for normal drops)
  dungeon_bronze: [
    { itemId: 'health-potion', chance: 0.5 },
    { itemId: 'iron-sword', chance: 0.15 },
    { itemId: 'scimitar', chance: 0.10 },
    { itemId: 'leather-armor', chance: 0.12 },
    { itemId: 'iron-helm', chance: 0.10 },
    { itemId: 'iron-pauldrons', chance: 0.08 },
    { itemId: 'leather-boots', chance: 0.10 },
    { itemId: 'wooden-shield', chance: 0.08 },
    { itemId: 'ruby', chance: 0.15 },
    { itemId: 'amethyst', chance: 0.12 },
    { itemId: 'gold-nugget', chance: 0.18 },
  ],
  dungeon_silver: [
    { itemId: 'large-health-potion', chance: 0.6 },
    { itemId: 'fire-staff', chance: 0.15 },
    { itemId: 'katana', chance: 0.10 },
    { itemId: 'halberd', chance: 0.08 },
    { itemId: 'chain-mail', chance: 0.15 },
    { itemId: 'knight-helm', chance: 0.12 },
    { itemId: 'steel-spaulders', chance: 0.12 },
    { itemId: 'chain-gauntlets', chance: 0.10 },
    { itemId: 'iron-shield', chance: 0.10 },
    { itemId: 'midnight-shield', chance: 0.08 },
    { itemId: 'forest-shield', chance: 0.07 },
    { itemId: 'mage-cloak', chance: 0.08 },
    { itemId: 'navy-cape', chance: 0.06 },
    { itemId: 'frost-cape', chance: 0.06 },
    { itemId: 'sapphire', chance: 0.20 },
    { itemId: 'diamond', chance: 0.10 },
    { itemId: 'gold-bar', chance: 0.15 },
    { itemId: 'silver-key', chance: 0.05 },
  ],
  dungeon_gold: [
    { itemId: 'large-health-potion', chance: 0.8 },
    { itemId: 'crystal-staff', chance: 0.20 },
    { itemId: 'great-bow', chance: 0.15 },
    { itemId: 'trident', chance: 0.12 },
    { itemId: 'legion-armor', chance: 0.15 },
    { itemId: 'barbarian-helm', chance: 0.12 },
    { itemId: 'legion-shoulders', chance: 0.10 },
    { itemId: 'plate-greaves', chance: 0.10 },
    { itemId: 'plate-boots', chance: 0.10 },
    { itemId: 'spartan-shield', chance: 0.10 },
    { itemId: 'golden-shield', chance: 0.06 },
    { itemId: 'shadow-shield', chance: 0.06 },
    { itemId: 'royal-cape', chance: 0.08 },
    { itemId: 'purple-cape', chance: 0.06 },
    { itemId: 'golden-cape', chance: 0.06 },
    { itemId: 'rose-cape', chance: 0.05 },
    { itemId: 'ruby-necklace', chance: 0.10 },
    { itemId: 'emerald-ring', chance: 0.10 },
    { itemId: 'sapphire', chance: 0.30 },
    { itemId: 'diamond', chance: 0.20 },
    { itemId: 'gold-bar', chance: 0.25 },
    { itemId: 'gold-key', chance: 0.05 },
  ]
};

/**
 * Mystery reward pools for the dungeon key trade, per key tier.
 * Bronze keys yield uncommon/rare items, silver yields rare/epic, gold yields epic/legendary.
 * Weighted random pick — higher weight = more likely.
 */
export const MYSTERY_REWARDS_BY_TIER = {
  bronze: [
    // Weapons
    { itemId: 'steel-blade',        weight: 12 },
    { itemId: 'battle-axe',         weight: 10 },
    { itemId: 'scimitar',           weight: 10 },
    { itemId: 'flail',              weight: 10 },
    { itemId: 'recurve-bow',        weight: 10 },
    { itemId: 'crossbow',           weight: 10 },
    { itemId: 'gnarled-staff',      weight: 10 },
    { itemId: 'loop-staff',         weight: 10 },
    { itemId: 'serpent-staff',      weight: 8 },
    { itemId: 'longspear',          weight: 10 },
    { itemId: 'halberd',            weight: 10 },
    // Armor
    { itemId: 'iron-helm',          weight: 10 },
    { itemId: 'barbarian-helm',     weight: 10 },
    { itemId: 'wizard-hat',         weight: 8 },
    { itemId: 'chain-mail',         weight: 10 },
    { itemId: 'iron-shield',        weight: 10 },
    { itemId: 'crimson-shield',     weight: 8 },
    { itemId: 'emerald-shield',     weight: 8 },
    { itemId: 'spartan-shield',     weight: 8 },
    { itemId: 'chain-gauntlets',    weight: 10 },
    { itemId: 'iron-boots',         weight: 10 },
    { itemId: 'steel-spaulders',    weight: 8 },
    { itemId: 'mantle',             weight: 8 },
    { itemId: 'green-cloak',        weight: 8 },
    { itemId: 'white-cloak',        weight: 8 },
    { itemId: 'maroon-cloak',       weight: 8 },
    { itemId: 'chain-leggings',     weight: 10 },
    // Accessories & treasures
    { itemId: 'gold-ring',          weight: 8 },
    { itemId: 'iron-amulet',        weight: 8 },
    { itemId: 'ruby',               weight: 8 },
    { itemId: 'large-health-potion',weight: 15 },
    // Rare bonus
    { itemId: 'golden-sword',       weight: 5 },
    { itemId: 'knight-helm',        weight: 5 },
    { itemId: 'fire-staff',         weight: 5 },
    { itemId: 'katana',             weight: 4 },
  ],
  silver: [
    // Weapons
    { itemId: 'golden-sword',       weight: 10 },
    { itemId: 'frost-blade',        weight: 8 },
    { itemId: 'fire-staff',         weight: 8 },
    { itemId: 'crystal-staff',      weight: 8 },
    { itemId: 'serpent-staff',      weight: 7 },
    { itemId: 'katana',             weight: 8 },
    { itemId: 'trident',            weight: 8 },
    { itemId: 'scythe',             weight: 8 },
    { itemId: 'golden-dagger',      weight: 8 },
    { itemId: 'great-bow',          weight: 8 },
    // Armor
    { itemId: 'knight-helm',        weight: 8 },
    { itemId: 'plate-armor',        weight: 8 },
    { itemId: 'plate-gauntlets',    weight: 8 },
    { itemId: 'tower-shield',       weight: 8 },
    { itemId: 'royal-shield',       weight: 7 },
    { itemId: 'midnight-shield',    weight: 7 },
    { itemId: 'forest-shield',      weight: 7 },
    { itemId: 'plate-greaves',      weight: 8 },
    { itemId: 'steel-sabatons',     weight: 8 },
    { itemId: 'plate-boots',        weight: 8 },
    { itemId: 'golden-shoulders',   weight: 7 },
    { itemId: 'legion-shoulders',   weight: 7 },
    { itemId: 'epaulets',           weight: 7 },
    { itemId: 'mage-cloak',         weight: 7 },
    { itemId: 'navy-cape',          weight: 7 },
    { itemId: 'forest-cape',        weight: 7 },
    { itemId: 'frost-cape',         weight: 7 },
    { itemId: 'royal-cape',         weight: 6 },
    // Accessories & treasures
    { itemId: 'emerald-ring',       weight: 7 },
    { itemId: 'ruby-necklace',      weight: 7 },
    { itemId: 'sapphire',           weight: 8 },
    { itemId: 'gold-bar',           weight: 8 },
    // Epic bonus
    { itemId: 'dungeon-slayer',     weight: 4 },
    { itemId: 'keymasters-ward',    weight: 4 },
    { itemId: 'diamond-staff',      weight: 4 },
    { itemId: 'dragonspear',        weight: 4 },
  ],
  gold: [
    // Legendary
    { itemId: 'hellfire-blade',      weight: 10 },
    { itemId: 'abyssal-crown',       weight: 10 },
    // Epic weapons
    { itemId: 'dungeon-slayer',      weight: 12 },
    { itemId: 'frost-blade',         weight: 10 },
    { itemId: 'diamond-staff',       weight: 10 },
    { itemId: 'dragonspear',         weight: 10 },
    // Epic armor
    { itemId: 'keymasters-ward',     weight: 12 },
    { itemId: 'golden-armor',        weight: 10 },
    { itemId: 'crusader-shield',     weight: 8 },
    { itemId: 'golden-shield',       weight: 8 },
    { itemId: 'shadow-shield',       weight: 8 },
    // Epic cloaks/capes
    { itemId: 'void-cloak',          weight: 10 },
    { itemId: 'shadow-cloak',        weight: 8 },
    { itemId: 'purple-cape',         weight: 8 },
    { itemId: 'golden-cape',         weight: 8 },
    { itemId: 'rose-cape',           weight: 7 },
    { itemId: 'royal-cape',          weight: 7 },
    // Epic accessories
    { itemId: 'ring-of-the-depths',  weight: 12 },
    { itemId: 'ring-of-power',       weight: 10 },
    { itemId: 'sapphire-pendant',    weight: 8 },
    // Treasures
    { itemId: 'diamond',             weight: 10 },
  ],
};
